import { http } from '../api/http';

export interface UploadTransferProofRequest {
  developmentId: number;
  amount: number;
  currency: 'MXN' | 'USD';
  file: File;
}

export interface TransferProof {
  id: string;
  userId: string;
  developmentId: string;
  amount: number;
  currency: string;
  fileUrl: string;
  fileName: string;
  fileSize: number;
  mimeType: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  rejectionReason?: string;
  adminNotes?: string;
  reviewedAt?: string;
  createdAt: string;
  updatedAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
  development?: {
    id: string;
    title: string;
  };
  reviewer?: {
    id: string;
    name: string;
  };
}

export interface TransferProofListResponse {
  data: TransferProof[];
  total: number;
  page: number;
  limit: number;
}

export const TransferProofService = {
  async upload(data: UploadTransferProofRequest): Promise<TransferProof> {
    const formData = new FormData();
    formData.append('developmentId', String(data.developmentId));
    formData.append('amount', String(data.amount));
    formData.append('currency', data.currency);
    formData.append('file', data.file);

    const response = await fetch(`${import.meta.env.VITE_API_URL}/transfer-proofs`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers: {
        // No establecer Content-Type, el navegador lo hace automáticamente con el boundary
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Error al subir comprobante');
    }

    return response.json();
  },

  async getAll(filters?: { 
    status?: string; 
    dateFrom?: string; 
    dateTo?: string;
    page?: number;
    limit?: number;
  }): Promise<TransferProofListResponse> {
    const params = new URLSearchParams();
    if (filters?.status) params.append('status', filters.status);
    if (filters?.dateFrom) params.append('dateFrom', filters.dateFrom);
    if (filters?.dateTo) params.append('dateTo', filters.dateTo);
    if (filters?.page) params.append('page', String(filters.page));
    if (filters?.limit) params.append('limit', String(filters.limit));

    return http<TransferProofListResponse>(`/transfer-proofs?${params.toString()}`);
  },

  async getById(id: string): Promise<TransferProof> {
    return http<TransferProof>(`/transfer-proofs/${id}`);
  },

  async review(
    id: string, 
    data: { 
      status: 'APPROVED' | 'REJECTED'; 
      rejectionReason?: string; 
      adminNotes?: string;
    }
  ): Promise<TransferProof> {
    return http<TransferProof>(`/transfer-proofs/${id}/review`, {
      method: 'PATCH',
      body: data,
    });
  },
};
